<template>
  <div
    class="banner"
    :style="{
      backgroundImage: `url(${transformImage(detail?.data?.background_cover_url || defaultBg, {
        height: 400,
      })})`,
    }"
  >
    <div class="content">
      <div v-if="editable" class="op-change-bg" @click="handleBgChangeClick">
        <iconpark-icon name="camera-e4706egp" />
      </div>
      <div class="info">
        <div class="logo" @click="handleAvatarChangeClick">
          <Resource
            :src="detail?.data?.avatar_url || defaultAvatar"
            class="w-full h-full object-fit"
          />
          <div v-if="editable" class="op-change-avatar">
            <iconpark-icon class="camera" name="camera-e4706egp" />
          </div>
        </div>
        <div class="text-[20px] font-medium mt-[16px] name">
          <a-input
            v-if="nameEditing"
            ref="nameRef"
            class="inline"
            autofocus
            :default-value="detail?.data?.name"
            @blur="handleNameChange"
          ></a-input>
          <span v-else>{{ detail?.data?.name }}</span>
          <iconpark-icon
            v-if="editable && !nameEditing"
            name="edit"
            class="edit inline ml-[4px]"
            @click="handleEditNameClick"
          >
          </iconpark-icon>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { usePersonalPageEditable } from './hooks';
import {
  getCompanyInfo,
  putUserOrgUpdateOrgLogoOrName,
  putUserOrgBackgroundCover,
} from '~/api/module/auth.ts';
import { useCustomUpload } from '~/hooks/useModal/modals/customCover/index.tsx';
import { useAvatarUpload } from '~/hooks/useModal/modals/avatar/index.tsx';

const userInfo = useUserInfo();
const { $t } = useNuxtApp();

// const detail = ref<CompanyInfoData>({
//   name: '',
//   avatar_url: '',
//   background_cover_url: '',
// })

const route = useRoute();
const editable = usePersonalPageEditable();
const nameRef = ref<HTMLInputElement>();
const nameEditing = ref(false);

const defaultAvatar = getBeUrl(
  isGlobalVersion()
    ? '/youyan/images/default/rendora_studio.jpeg'
    : '/youyan/images/default/personal_logo.jpeg',
);

const defaultBg = getBeUrl('/youyan/images/default/personal_template_bg.png');

const handleBgChangeClick = () => {
  useCustomUpload({
    title: $t('shang-chuan-bei-jing-tu'),
    tip: `${$t('xian-zhi-3mb-yi-nei-jin-zhi-chi')} <b>jpg, png</b>`,
    url: detail.value?.data?.background_cover_url || '',
    defaultUrl: defaultBg,
    request: path =>
      putUserOrgBackgroundCover({
        background_cover_path: path!,
        org_id: userInfo.value!.organization_id,
      }),
    success() {
      execute();
    },
  });
};

const handleNameChange = async (e: any) => {
  await putUserOrgUpdateOrgLogoOrName({ name: e.target!.value });
  nameEditing.value = false;
  await execute();
};

const { data: detail, execute } = useAsyncData(() =>
  getCompanyInfo({ id: route.params.key as string }),
);

const handleEditNameClick = () => {
  nameEditing.value = !nameEditing.value;
  nextTick(() => {
    nameRef.value?.focus();
  });
};

const handleAvatarChangeClick = () => {
  if (!editable.value) {
    return;
  }
  useAvatarUpload({
    title: $t('shang-chuan-tou-xiang'),
    tip: `${$t('xian-zhi-3mb-yi-nei-jin-zhi-chi')} <b>jpg, png</b>`,
    url: detail.value?.data?.avatar_url || '',
    defaultUrl: defaultAvatar,
    request: path =>
      putUserOrgUpdateOrgLogoOrName({
        logo_path: path,
        org_id: userInfo.value!.organization_id,
      }),
    success(data: any) {
      execute();
    },
  });
};
</script>

<style lang="scss" scoped>
.edit {
  font-size: 14px;
  cursor: pointer;
}
.banner {
  @apply w-full;
  height: 200px;
  position: relative;
  background-size: 100% 100%;
  background-repeat: no-repeat;
  background-position: center center;
  margin-bottom: 96px;
  .content {
    margin: 0 auto;
    padding: 0 160px 0;
    position: relative;
  }
  .info {
    position: absolute;
    display: flex;
    top: 140px;
    font-size: 20px;
    color: var(--text-1);
    line-height: 28px;
    .name {
      transform: translateY(60px) translateX(20px);
      .edit {
        display: none;
      }
      &:hover {
        .edit {
          display: inline-block;
          font-size: 16px;
        }
      }
    }
  }
  .logo {
    @apply h-[120px] w-[120px] rounded-full overflow-hidden;
    border: 2px solid rgba(255, 255, 255, 1);
    box-shadow: 0px 12px 20px 0px rgba(105, 98, 255, 0.25);
    &:hover .op-change-avatar {
      display: flex;
    }
  }
  .op-change-avatar {
    display: none;
    position: absolute;
    left: 0;
    top: 0;
    cursor: pointer;
    width: 120px;
    height: 120px;
    background: rgba(0, 0, 0, 0.3);
    border-radius: 50%;
    justify-content: center;
    align-items: center;
    .camera {
      font-size: 40px;
    }
  }
  .op-change-bg {
    cursor: pointer;
    position: absolute;
    right: 16px;
    top: 150px;
    display: flex;
    justify-content: center;
    align-items: center;
    font-size: 20px;
    width: 32px;
    height: 32px;
    background: var(--text-1-20);
    border-radius: 6px;
    backdrop-filter: blur(3px);
    box-shadow: 0px 1px 18px 0px rgba(4, 14, 48, 0.1);

    &:hover {
      background: var(--text-1-60);
    }
  }
}
</style>
